// TravelCard.jsx
import React from "react";
import PropTypes from "prop-types";
import styles from "./style/comp1.module.css";
import { Tooltip } from "react-tooltip";
import { FaRoute } from "react-icons/fa";
import { Share2 } from "lucide-react";

const TravelCard = ({
  tag = "Best",
  departure = { location: "SC", time: "18:00", date: "11 May" },
  arrival = { location: "LHU", time: "14:00", date: "12 May" },
  duration = "11hrs 55mins",
  stops = "1 change stop",
  availability = "",
  price = 5650,
  currency = "₹",
  modes = [],
  toolTip = "",
  transportModes = ["bus"],
  onClick,
  isSelected,
  trace_id,
  result_idx,
  is_lcc,
  baggage,
  onViewMap,
  onShare,
}) => {
  // console.log('Debug: TravelCard received departure:', departure);
  // console.log('Debug: TravelCard received arrival:', arrival);

  const getTagStyle = (tagType) => {
    if (!tagType || !tagType.trim()) return styles.defaultTag;
    const t = tagType.trim().toLowerCase();
    if (t === "best") return styles.bestTag;
    if (t === "cheapest" || t === "cheapest available")
      return styles.cheapestTag;
    if (t === "fastest" || t === "fastest available") return styles.fastestTag;
    return styles.defaultTag;
  };

  const getTransportIcon = (mode) => {
    const icons = {
      bus: "🚌",
      train: "🚆",
      plane: "✈️",
      car: "🚗",
      ship: "🚢",
    };
    return icons[mode?.toLowerCase()] || "🚌";
  };

  const getIconTrans = (mode) => {
    const icons = {
      flight: <img src="/planeicon.svg" />,
      train: <img src="/trainicon.svg" />,
    };

    return icons[mode?.toLowerCase()] || "";
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat("en-IN").format(amount);
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick({ tag, departure, arrival, duration, stops, price });
    }
    // console.log("> travel card click:", modes);

  };

  const getAvailabilityColor = (availability) => {
    if (!availability) return "#64748b";
    if (availability.toLowerCase().includes("unavailable")) return "#DC2626"; // red
    if (
      availability.toLowerCase().includes("available") &&
      !availability.toLowerCase().includes("partial")
    )
      return "#16A34A"; // green
    if (availability.toLowerCase().includes("partial")) return "#CA8A04"; // orange
    return "#64748b";
  };

  const tooltipId = React.useMemo(
    () =>
      `availability-tooltip-${Math.random()
        .toString(36)
        .substr(2, 9)}-${Date.now()}`,
    []
  );

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "0",
        position: "relative",
        width: "100%",
      }}
    >
      <div
        className={`${styles.cardContainer} ${isSelected ? styles.selected : ""
          }`}
        onClick={handleCardClick}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleCardClick();
          }
        }}
      >
        <div className={styles.headerRow}>
          <div className={styles.passengerIcons} aria-label="Passenger journey">
            {modes &&
              modes.length > 0 &&
              modes.map((mode, idx) => (
                <React.Fragment key={idx}>
                  <span className={styles.passengerIconImg}>
                    {getIconTrans(mode)}
                  </span>
                  {idx < modes.length - 1 && (
                    <img
                      src="/arrow.svg"
                      alt="to"
                      className={styles.passengerArrow}
                    />
                  )}
                </React.Fragment>
              ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              className={styles.mapBtn}
              onClick={(e) => {
                e.stopPropagation();
                if (onViewMap) onViewMap();
              }}
              title="View Route on Map"
            >
              <FaRoute size={14} />
            </button>
            <button
              className={styles.mapBtn}
              onClick={(e) => {
                e.stopPropagation();
                if (onShare) onShare();
              }}
              title="Share this route"
            >
              <Share2 size={14} />
            </button>
            {tag && tag.trim() && (
              <div className={getTagStyle(tag)} aria-label={`${tag} option`}>
                {tag}
              </div>
            )}
          </div>
        </div>

        <div className={styles.travelInfo}>
          <div
            className={styles.journeyDetails}
            style={{ position: "relative" }}
          >
            <div className={styles.departure}>
              <div
                className={styles.location}
                aria-label="Departure location and time"
              >
                {departure.location}, {departure.time}
              </div>
              <div className={styles.date}>{departure.date}</div>
            </div>

            <div className={styles.journeyMeta}>
              <div className={styles.duration}>{duration}</div>
              <div className={styles.stops}>{stops}</div>
            </div>

            <div className={styles.arrival}>
              <div
                className={styles.location}
                aria-label="Arrival location and time"
              >
                {arrival.location}, {arrival.time}
              </div>
              <div className={styles.date}>{arrival.date}</div>
            </div>
            <svg
              className={styles.svgLine}
              width="100%"
              height="8"
              style={{
                position: "absolute",
                top: "40%",
                left: 0,
                pointerEvents: "none",
                zIndex: 0,
              }}
            >
              <line
                x1="25%"
                x2="75%"
                y1="4"
                y2="4"
                stroke="#a0aec0"
                strokeWidth="0.5"
                strokeDasharray="3,3"
              />
            </svg>
          </div>

          <div className={styles.bottomSection}>
            <div className={styles.bottomRow}>
              <div>
                {availability && (
                  <span
                    className={styles.availability}
                    style={{
                      color: getAvailabilityColor(availability),
                    }}
                  >
                    <span
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <img
                        src="/eicon.svg"
                        alt="info"
                        style={{
                          width: 18,
                          height: 18,
                          marginRight: 4,
                          marginBottom: 2,
                          display: "inline-block",
                          verticalAlign: "middle",
                        }}
                        data-tooltip-id={tooltipId}
                        data-tooltip-content={toolTip || ""}
                      />
                      <Tooltip
                        id={tooltipId}
                        place="bottom"
                        strategy="fixed"
                        style={{
                          backgroundColor: "#1F2937",
                          zIndex: 9999,
                          color: "white",
                          padding: "8px 12px",
                          borderRadius: "6px",
                          fontSize: "14px",
                          maxWidth: "320px",
                          textAlign: "center",
                          fontWeight: 600,
                        }}
                      />
                    </span>
                    {availability}
                  </span>
                )}
              </div>
              <div
                className={styles.price}
                aria-label={`Price \${currency}\${formatPrice(price)}`}
              >
                <span className={styles.rupeeSymbol}>{currency}</span>
                <span className={styles.amount}>{formatPrice(price)}</span>
                <span className={styles.priceArrow} aria-hidden="true">
                  ›
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isSelected && (
        <span className={styles.selectedArrow}>
          <svg width="12" height="24" viewBox="0 0 12 24" fill="none">
            <path d="M0 0L12 12L0 24V0Z" fill="#FF4E01" />
          </svg>
        </span>
      )}
    </div>
  );
};

// PropTypes for better development experience
TravelCard.propTypes = {
  tag: PropTypes.string,
  departure: PropTypes.shape({
    location: PropTypes.string.isRequired,
    time: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
  }),
  arrival: PropTypes.shape({
    location: PropTypes.string.isRequired,
    time: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
  }),
  duration: PropTypes.string,
  stops: PropTypes.string,
  price: PropTypes.number,
  currency: PropTypes.string,
  transportModes: PropTypes.arrayOf(PropTypes.string),
  onClick: PropTypes.func,
  isSelected: PropTypes.bool,
  toolTip: PropTypes.string,
  modes: PropTypes.arrayOf(PropTypes.string),
  availability: PropTypes.string,
  trace_id: PropTypes.string,
  result_idx: PropTypes.string,
  is_lcc: PropTypes.bool,
  baggage: PropTypes.shape({
    cabin_baggage: PropTypes.string,
    checkin_baggage: PropTypes.string,
  }),
};

export default TravelCard;
